<template>
	<div class="tab-pane active" id="step1">
		<h2 class="hidden-lg">{{ $t("tab.key_data") }}</h2>

    <!-- Desciption and Instructions -->
		<div class="row">
			<div class="col-md-12">
				<p><b>{{ $t("labels.description") }}</b></p>
				<p class="text-break introduction-bg" v-html="exhibitionDescription"></p>
				<Instructions />
				<hr>
			</div>
    </div>

    <!-- Booth Location -->
    <div class="row">
      <div class="col-md-6">
        <p><b>{{ $t("labels.select_place") }}:</b></p>
        <div class="form-group">
          <label for="exhibitionHall">
            {{ $t('forms.hall') }}{{ (exhibition.hall_settings.hall_name_required) ? '*' : ''}}:
          </label>
          <select v-if="exhibition.hall_settings.hall_name_selection_active" v-model="exhibitionHall"
            class="form-control">
            <option v-for="option,index in hallOptions" :key="index" :value="option.value">
              {{ option.label }}
            </option>
          </select>
          <input v-else
            type="text"
            class="form-control input-sm"
            id="exhibitionHall"
            v-model.trim="exhibitionHall"
            :placeholder='$t("forms.placeholders.hall")'
            :disabled="isEmbedded !== null">
        </div>
        <div class="form-group">
          <label for="exhibitionBoothNumber">
            {{ $t('forms.booth_number') }}{{ (exhibition.hall_settings.booth_name_required) ? '*' : ''}}:
          </label>
          <input
            type="text"
            class="form-control input-sm"
            id="exhibitionBoothNumber"
            v-model.trim="exhibitionBoothNumber"
            :placeholder='$t("forms.placeholders.booth_number")'
            :disabled="isEmbedded !== null">
        </div>
      </div>
    </div>

    <!-- Booth Size -->
    <div class="row">
      <div class="col-md-12"><hr></div>
			<div class="col-md-6">
				<p><b>{{ $t("labels.set_size") }}:</b></p>

        <!-- Width -->
				<label>{{ $t("labels.width") }} <small>(m)</small></label>
				<div class="input-group input-group-sm">
					<span class="input-group-btn">
						<button
							class="btn btn-primary btn-sm"
							tabindex="-1"
							type="button"
							@click="decreaseWidth"
							:disabled="basket.width <= 0">
							<i class="fa fa-minus" aria-hidden="true"></i>
						</button>
					</span>

					<input
						class="form-control"
						type="number"
						:step="STEP"
						:min="STEP"
						name="width"
						v-model="basket.width"
						@change="updateWidth">

					<span class="input-group-btn">
						<button
							class="btn btn-primary btn-sm"
							tabindex="-1"
							type="button"
							@click="increaseWidth">
							<i class="fa fa-plus" aria-hidden="true"></i>
						</button>
					</span>
				</div>

        <!-- Depth -->
				<label>{{ $t("labels.depth") }} <small>(m)</small></label>
				<div class="input-group input-group-sm">
					<span class="input-group-btn">
						<button
							class="btn btn-primary btn-sm"
							tabindex="-1"
							type="button"
							@click="decreaseDepth"
							:disabled="basket.depth <= 0">
							<i class="fa fa-minus" aria-hidden="true"></i>
						</button>
					</span>
					<input
						class="form-control"
						type="number"
						:step="STEP"
						:min="STEP"
						name="depth"
						v-model="basket.depth"
						@change="updateDepth">
					<span class="input-group-btn">
						<button class="btn btn-primary btn-sm" tabindex="-1" type="button" @click="increaseDepth"><i class="fa fa-plus" aria-hidden="true"></i></button>
					</span>
				</div>
			</div>
    </div>

    <!-- Booth Type -->
    <div class="row">
			<div class="col-md-12">
				<hr>
				<p><b>{{ $t("labels.select_booth_type") }}:</b></p>
				<p>{{ $t("configurator.descriptions.stand_type_selection") }}</p>
				<div class="col-xs-6 col-sm-2" v-for="boothType in availableBoothTypes" :key="boothType.type">
					<div>
						<div
							class="booth"
							:class="[boothType.css_class, (basket.boothType !== null && basket.boothType.type === boothType.type) ? 'active' : '']"
							@click="selectBoothType(boothType)">
						</div>
						<label class="label_item">{{ $t(boothType.translation_key) }}</label>
					</div>
				</div>
			</div>
    </div>

    <!-- Button Controls -->
    <div class="row">
			<div class="col-md-12" v-if="!isExhibitionClosed">
					<hr>
				<div class="wizard-footer">
					<div class="pull-right" v-show="isKeyDataSelected">
						<button
							type='button'
							class='btn btn-next btn-warning btn-key-data'
							name='next'
							@click="store.updateCurrentTab('booths')">
							{{ $t("tab.booth_selection") + " " + $t("buttons.next_to") }}
						</button>
					</div>
					<div class="clearfix"></div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import _ from 'lodash'
import { computed, ref, watch } from 'vue'
import { useMainStore } from '@/store'
import { BOOTH_TYPES } from '@/constants'
import BoothType from '@/classes/BoothType'
import Instructions from '@/components_new/Configurator/Instructions.vue'

const STEP = 0.25

const store = useMainStore()

// define passed props
const props = defineProps({
  basket: {
    type: Object,
    required: true,
  },
  exhibition: {
    type: Object,
    required: true,
  },
  isExhibitionClosed: {
    type: Boolean,
    default: false,
  },
  isEmbedded: {
		required: true
	},
})

// define emits
const emit = defineEmits(['saveBasket'])

// define computed props
const locale = computed(() => store.settings.locale)
const shoppingCart = computed(() => store.settings.shopping_cart)

// ref
const exhibitionHall = ref(props.basket.exhibitionHall ?? '')
const exhibitionBoothNumber = ref(props.basket.exhibitionBoothNumber ?? '')

const inEnglish = computed(() => {
  return locale.value === 'en'
})

const exhibitionDescription = computed(() => {
	return (inEnglish.value)
		? props.exhibition.description_en
		: props.exhibition.description
})

const availableBoothTypes = computed(() => {
	if (
		props.exhibition === undefined
		|| props.exhibition === null
		|| props.exhibition.booth_types === undefined
		|| props.exhibition.booth_types === null
	) {
		return []
	}

	return BOOTH_TYPES.filter(boothType => {
		return props.exhibition.booth_types.includes(boothType.type)
	})
})

const hallOptions = computed(() => {
	if (props.exhibition.hall_settings.hall_name_selection_active) {
		return [{
				label: (inEnglish.value) ? 'Please select' : 'Bitte auswählen',
				value: ''
			}]
			.concat(props.exhibition.hall_settings.hall_options.split(';')
				.filter(item => {
					return item.trim().length > 0
				})
				.map(item => {
					return {
						label: item,
						value: item
					}
				})
			)
	}

	return []
})

/**
 * Computes whether all required data is selected to show the next button
 */
const isKeyDataSelected = computed(() => {
  // Check for exhibition hall and booth number - if required
  if (props.exhibition.hall_settings.hall_name_required && !props.basket.exhibitionHall) {
    return false
  }
  if (props.exhibition.hall_settings.booth_name_required && !props.basket.exhibitionBoothNumber) {
    return false
  }

  // Check for booth size/type configuration
	return (shoppingCart.value.width && shoppingCart.value.width > 0)
		&& (shoppingCart.value.depth && shoppingCart.value.depth > 0)
		&& shoppingCart.value.booth_type !== null
})

// define watchers
watch(() => exhibitionHall.value, () => {
	setExhibitionHall()
})
watch(() => exhibitionBoothNumber.value, () => {
	setExhibitionBoothNumber()
})

// define methods
/**
 * @param {BoothType} boothType
 */
const selectBoothType = (boothType) => {
	props.basket.boothType = boothType
	shoppingCart.value.setBoothType(boothType)
	
	emit('saveBasket')
}

const increaseDepth = () => {
	props.basket.depth = props.basket.depth !== '' ? parseFloat(props.basket.depth) + STEP : STEP
	shoppingCart.value.increaseDepth(STEP)

	props.basket.computed.squareMeter = shoppingCart.value.getSquareMeter()

	emit('saveBasket')
}

const decreaseDepth = () => {
	props.basket.depth = props.basket.depth !== '' ? parseFloat(props.basket.depth) - STEP : 0
	shoppingCart.value.decreaseDepth(STEP)

	props.basket.computed.squareMeter = shoppingCart.value.getSquareMeter()
	emit('saveBasket')
}

const increaseWidth = () => {
	props.basket.width = props.basket.width !== '' ? parseFloat(props.basket.width) + STEP : STEP
	shoppingCart.value.increaseWidth(STEP)

	props.basket.computed.squareMeter = shoppingCart.value.getSquareMeter()
	emit('saveBasket')
}

const decreaseWidth = () => {
	props.basket.width = props.basket.width !== '' ? parseFloat(props.basket.width) - STEP : 0
	shoppingCart.value.decreaseWidth(STEP)

	props.basket.computed.squareMeter = shoppingCart.value.getSquareMeter()
	emit('saveBasket')
}

const validateWidth = () => {
	let value = parseFloat(props.basket.width);
	if (value % STEP !== 0) {
		props.basket.width = Math.round(value / STEP) * STEP;
	}
}

const validateDepth = () => {
	let value = parseFloat(props.basket.depth);
	if (value % STEP !== 0) {
		props.basket.depth = Math.round(value / STEP) * STEP;
	}
}

const updateDepth = () => {
	validateDepth();
	shoppingCart.value.setDepth(props.basket.depth)
}

const updateWidth = () => {
	validateWidth();
	shoppingCart.value.setWidth(props.basket.width)
}

const setExhibitionHall = _.debounce(() => {
		props.basket.exhibitionHall = props.isEmbedded 
      ? props.basket.exhibitionHall 
      : exhibitionHall.value
	},
	500
)

const setExhibitionBoothNumber = _.debounce(() => {
		props.basket.exhibitionBoothNumber = props.isEmbedded
			? props.basket.exhibitionBoothNumber
			: exhibitionBoothNumber.value
	},
	500
)
</script>
